/**
 * OmniLeagues — AD Organisations Hub.
 *
 * Sub-tabs:
 *   • Profile    — org identity, logo, address, contact.
 *   • Leadership — Board / Operations / Steering (per-sport tabs) editor.
 *   • Coaches    — per-sport → team-level → role cards editor.
 *   • Background Checks — per coach per season (COACH_BACKGROUND_CHECKS.md).
 *
 * Leadership + Coaches read/write the SAME `staff_positions` SOT the TS
 * WP-Mgmt Staff tabs use (via /api/ad-staff → ol_canonical.ensure_position).
 */
import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Building2, Crown, Users, ShieldCheck } from 'lucide-react';

import OrgProfilePanel from './org/OrgProfilePanel';
import OrgLeadershipPanel from './org/OrgLeadershipPanel';
import OrgCoachesPanel from './org/OrgCoachesPanel';
import OrgBackgroundChecksPanel from './org/OrgBackgroundChecksPanel';

const TABS = [
  { key: 'profile',    label: 'Profile',    icon: Building2 },
  { key: 'leadership', label: 'Leadership', icon: Crown },
  { key: 'coaches',    label: 'Coaches',    icon: Users },
  { key: 'checks',     label: 'Background Checks', icon: ShieldCheck },
];

export default function OrganizationsHubPanel({ orgId, isDark }) {
  const [active, setActive] = useState('profile');

  if (!orgId) {
    return (
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
        <CardContent className="p-6">
          <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No organization selected.</p>
        </CardContent>
      </Card>
    );
  }

  const navBg = isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200';
  const inactive = isDark
    ? 'text-slate-400 hover:text-white hover:bg-slate-700/40'
    : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100';
  const activeCls = isDark
    ? 'bg-amber-600/20 text-amber-400 border-amber-500/40'
    : 'bg-amber-50 text-amber-700 border-amber-200';

  return (
    <div className="space-y-4" data-testid="organizations-hub">
      <div className={`flex flex-wrap items-center gap-1 p-1.5 rounded-lg border ${navBg}`}
           data-testid="org-hub-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          const isActive = active === t.key;
          return (
            <Button key={t.key} variant="ghost" size="sm" onClick={() => setActive(t.key)}
                    className={`h-9 px-4 text-sm font-medium border ${isActive ? activeCls : 'border-transparent ' + inactive}`}
                    data-testid={`org-hub-tab-${t.key}`}>
              <Icon className="h-4 w-4 mr-2" />
              {t.label}
            </Button>
          );
        })}
      </div>

      {active === 'profile' && <OrgProfilePanel orgId={orgId} isDark={isDark} showContact={false} />}
      {active === 'leadership' && <OrgLeadershipPanel orgId={orgId} isDark={isDark} />}
      {active === 'coaches' && <OrgCoachesPanel orgId={orgId} isDark={isDark} />}
      {active === 'checks' && <OrgBackgroundChecksPanel orgId={orgId} />}
    </div>
  );
}
